<template>
    <b-modal
        v-model="templateRelationDetails.isOpen"
        modal-class="vue-need-bootstrap"
        header-class="px-3"
        title-class="font-weight-bold"
        body-class="pb-1"
        ok-only
        scrollable
        centered
        :title="getTitle"
        size="lg"
        ok-title="Close"
        @ok="onCloseHandler"
        @close="onCloseHandler"
    >
        <div class="content px-1">
            <div v-if="templateRelationDetails.showDeleteMessage" class="pb-4">
                <h4 class="h6 delete-title-message font-weight-normal mb-2">
                    Templates currently in use in the product(s) listed below.
                </h4>
                <p class="delete-subtitle-message mb-0">
                    To delete the template, open the associated product(s) and unlink/remove the connected template.
                </p>
            </div>

            <div v-if="getProducts.length > 0">
                <div class="pb-1">
                    <h6 class="text-body font-weight-bold text-black mb-0">Linked products:</h6>
                </div>

                <template v-for="(item, index) in getProducts">
                    <template-relation-details-modal-item :key="index" :item="item" type="product" class="d-flex" />
                    <div v-if="index < getProducts.length - 1" :key="index + '-divider'" class="divider" />
                </template>
            </div>
        </div>
    </b-modal>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { BModal } from 'bootstrap-vue';
import TemplateRelationDetailsModalItem from './item/TemplateRelationDetailsModalItem.vue';
import { ITemplateRelationDetails, IProduct } from '@/typings/templateRelationDetails/templateRelationDetails';

const props = defineProps<{
    templateRelationDetails: ITemplateRelationDetails;
}>();

const emit = defineEmits(['close']);

const iFrameUrl = process.env.VUE_APP_SHOPIFY_APP_URL;
const customilyBaseUrl = process.env.VUE_APP_CUSTOMILY_BASE_URL;

const getTitle = computed((): string => {
    return props.templateRelationDetails?.title;
});

const getProducts = computed(() => {
    return props.templateRelationDetails.Products.map((item) => {
        const client = props.templateRelationDetails?.Clients?.find((client) => client.client_id === item.client_id);
        return {
            title: item.product_title,
            src: item.image_src,
            published: item.published,
            active: item.active,
            url: `${customilyBaseUrl}/store/product?product=${item.sp_product_id}&shop=${client?.client_actual_domain}`,
            childrens: getOptionSetsAndVariants(item)
        };
    });
});

const getOptionSetsAndVariants = (item: IProduct) => {
    const client = props.templateRelationDetails?.Clients?.find((client) => client.client_id === item.client_id);
    const productUrl = `${customilyBaseUrl}/store/product?product=${item.sp_product_id}&shop=${client?.client_actual_domain}`;

    const variations = [];

    // Option level functions: the template is assigned to an option value (ie. Size > S)
    for (const variation of item?.configuration?.variations || []) {
        if (!variation.values?.length) {
            continue;
        }

        for (const value of variation.values) {
            if (value.product_id === null || value.product_id !== props.templateRelationDetails?.templateId) {
                continue;
            }
            variations.push({
                title: `${variation.name} > ${value?.value || 'N/A'}`,
                url: productUrl,
                type: 'variant'
            });
        }
    }

    // Variant level functions: the template is assigned to a variant combination (ie. azul / S / Algodon)
    for (const confVariant of item?.configuration?.conf_variants || []) {
        for (const confVariantFunction of confVariant.functions || []) {
            if (confVariantFunction.function?.type !== 'product') {
                continue;
            }
            if (confVariantFunction.value?.product_id !== props.templateRelationDetails?.templateId) {
                continue;
            }
            const title =
                confVariant.title ||
                [confVariant.option1, confVariant.option2, confVariant.option3].filter(Boolean).join(' / ');
            variations.push({
                title: title || 'N/A',
                url: productUrl,
                type: 'variant'
            });
        }
    }

    const optionSets = (item.configuration?.assignedSets || []).map((assignedSet) => {
        const optionSet = props.templateRelationDetails.OptionSets?.find(
            (optionSet) => optionSet.optionset_internal_id === assignedSet.id
        );
        if (!optionSet) {
            return [];
        }
        return (optionSet.options || []).map((option) => ({
            title: `${optionSet.optionset_name} > ${option.option_label ?? 'N/A'}`,
            url: `${iFrameUrl}/set?set=${optionSet.optionset_internal_id}`,
            type: 'option'
        }));
    });

    const flattenedOptionSets = optionSets.flat();

    return variations.concat(flattenedOptionSets);
};

const onCloseHandler = () => {
    emit('close');
};
</script>

<style scoped lang="scss">
::v-deep .scopes-update-wrapper {
    .modal-header {
        background: #f5f5f5;
        justify-content: center;
        border: 0;
    }
}

.delete-title-message {
    color: #ff5252;
}

.delete-subtitle-message {
    color: #6c757d;
}

.divider {
    padding: 0 0.75rem;
    border-top: 1px solid #dee2e6;
}
</style>
